/**
 * React Query hooks for Service Accounts management.
 *
 * Service accounts are OAuth2 client credentials tied to a tenant,
 * used for M2M API access. Backend: /api/v1/tenants/{tenantId}/service-accounts
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { useTenant } from '@/contexts/TenantContext';

// =============================================================================
// Types
// =============================================================================

export type ServiceAccountStatus = 'active' | 'suspended' | 'revoked';

export type AllowedScope =
  | 'agents:view' | 'agents:use' | 'agents:edit' | 'agents:admin'
  | 'knowledge:view' | 'knowledge:contribute' | 'knowledge:admin'
  | 'mcp:use' | 'mcp:admin';

export interface ServiceAccount {
  id: string;
  client_id: string;
  tenant_id: string;
  created_by: string;
  name: string;
  description: string | null;
  scopes: AllowedScope[];
  status: ServiceAccountStatus;
  last_rotated_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ServiceAccountCreated extends ServiceAccount {
  /** One-time secret shown only at creation. Store it securely! */
  client_secret: string;
}

export interface RotatedSecret {
  client_id: string;
  client_secret: string;
}

export interface ServiceAccountListResponse {
  data: ServiceAccount[];
  total: number;
  page: number;
  page_size: number;
}

export interface ServiceAccountCreate {
  name: string;
  description?: string;
  scopes: AllowedScope[];
}

export interface ServiceAccountUpdate {
  name?: string;
  description?: string;
  status?: ServiceAccountStatus;
}

// =============================================================================
// Query Keys
// =============================================================================

const SA_KEY = 'service-accounts';

// =============================================================================
// Helpers
// =============================================================================

function useServiceAccountsBase() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return { tenantId };
}

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Fetch all service accounts for the current tenant.
 *
 * ``search`` (REQ-FRT-004) filters server-side by name or client id for the
 * identity selector of the frontier routine editor.
 */
export function useServiceAccounts(options?: {
  page?: number;
  page_size?: number;
  search?: string;
}) {
  const { tenantId } = useServiceAccountsBase();

  return useQuery({
    queryKey: [SA_KEY, tenantId, options],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (options?.page) params.set('page', options.page.toString());
      if (options?.page_size) params.set('page_size', options.page_size.toString());
      if (options?.search) params.set('search', options.search);
      const qs = params.toString();
      const { data } = await apiClient.get<ServiceAccountListResponse>(
        `/api/v1/service-accounts${qs ? `?${qs}` : ''}`
      );
      return data;
    },
    enabled: !!tenantId,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Create a new service account.
 * Returns one-time client_secret in ServiceAccountCreated.
 */
export function useCreateServiceAccount() {
  const { tenantId } = useServiceAccountsBase();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ServiceAccountCreate) => {
      const { data } = await apiClient.post<ServiceAccountCreated>('/api/v1/service-accounts', input);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SA_KEY, tenantId] });
    },
  });
}

/**
 * Update name, description, or status of a service account.
 */
export function useUpdateServiceAccount(id: string) {
  const { tenantId } = useServiceAccountsBase();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ServiceAccountUpdate) => {
      const { data } = await apiClient.patch<ServiceAccount>(`/api/v1/service-accounts/${id}`, input);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SA_KEY, tenantId] });
    },
  });
}

/**
 * Delete a service account.
 */
export function useDeleteServiceAccount() {
  const { tenantId } = useServiceAccountsBase();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/v1/service-accounts/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SA_KEY, tenantId] });
    },
  });
}

/**
 * Rotate the client secret of a service account.
 * Returns new one-time secret.
 */
export function useRotateServiceAccountSecret(id: string) {
  const { tenantId } = useServiceAccountsBase();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<RotatedSecret>(`/api/v1/service-accounts/${id}/rotate-secret`, {});
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SA_KEY, tenantId] });
    },
  });
}

// =============================================================================
// Scope helpers
// =============================================================================

export const ALL_SCOPES: { value: AllowedScope; label: string; group: string }[] = [
  { value: 'agents:view',   label: 'View agents',           group: 'Agents' },
  { value: 'agents:use',    label: 'Use agents (chat)',      group: 'Agents' },
  { value: 'agents:edit',   label: 'Edit agents',           group: 'Agents' },
  { value: 'agents:admin',  label: 'Admin agents',          group: 'Agents' },
  { value: 'knowledge:view',        label: 'View knowledge',        group: 'Knowledge' },
  { value: 'knowledge:contribute',  label: 'Contribute knowledge',  group: 'Knowledge' },
  { value: 'knowledge:admin',       label: 'Admin knowledge',       group: 'Knowledge' },
  { value: 'mcp:use',   label: 'Use MCP tools',   group: 'MCP' },
  { value: 'mcp:admin', label: 'Admin MCP tools', group: 'MCP' },
];
